import { getUniqueReactions } from '../reactions/reactionUtil';
import type { TaskItemChipAttrs } from '../../../common/tiptap/extensions/taskList/taskListItemChipTypes';
import type { ElementChipDescriptor } from './elementChipsTypes';

/**
 * Chip-relevant task item node attrs. `checked` comes from the base task item
 * schema (not the chip attrs) and is used for the due-date chip's complete state.
 */
export type TaskItemChipSource = TaskItemChipAttrs & { checked?: boolean };

/** Updates a subset of the owning task node's attrs (the node view's `updateAttributes`). */
export type UpdateTaskItemAttrs = (attrs: Partial<TaskItemChipAttrs>) => void;

/**
 * The container ID that keys a chip's selection/editing/popover state. For task nodes this is the
 * stable `taskId`. It can briefly be `null` before the id plugin stamps it, in
 * which case the chip still renders but isn't keyed (selection wiring is Step 4).
 */
const getContainerId = (taskId: string | null): string => taskId ?? '';

const getTaskDueDateChipDescriptor = ({ taskId, dueDate, checked }: TaskItemChipSource): ElementChipDescriptor[] => {
    if (!dueDate) return [];

    return [
        {
            type: 'due-date',
            id: 'due-date',
            activationMode: 'select',
            containerId: getContainerId(taskId),
            dueReminder: dueDate.reminder,
            dueTimestamp: dueDate.timestamp,
            hasDueDateTime: dueDate.hasTime,
            isComplete: Boolean(checked),
        },
    ];
};

const getTaskAssignmentChipDescriptors = ({
    taskId,
    assignments,
    showAssignments,
}: TaskItemChipSource): ElementChipDescriptor[] => {
    // Mirror the element `showAssignments` gate: when off, no assignment chips at
    // all; when on with no assignees, the empty "Assign to" placeholder.
    if (!showAssignments) return [];

    const containerId = getContainerId(taskId);

    if (assignments.length === 0) {
        return [
            {
                type: 'assignment-placeholder',
                id: 'assignment-placeholder',
                activationMode: 'select',
                containerId,
            },
        ];
    }

    return assignments.reduce<ElementChipDescriptor[]>((descriptors, { assigneeId, assigneeName }) => {
        if (!assigneeId || !assigneeName) return descriptors;

        descriptors.push({
            type: 'assignment',
            id: `assignment-${assigneeId}`,
            activationMode: 'select',
            containerId,
            assigneeId,
            assigneeName,
        });

        return descriptors;
    }, []);
};

const getTaskLabelChipDescriptors = ({ labels }: TaskItemChipSource): ElementChipDescriptor[] =>
    labels.reduce<ElementChipDescriptor[]>((descriptors, { labelId, name, colour }) => {
        if (!labelId) return descriptors;

        descriptors.push({
            type: 'label',
            id: `label-${labelId}`,
            activationMode: 'select',
            // Denormalised display cache off the node attr (re-syncable from redux
            // by id later), shaped to what the label view renders.
            label: { _id: labelId, name, color: colour },
        });

        return descriptors;
    }, []);

const getTaskReactionChipDescriptors = ({ reactions }: TaskItemChipSource): ElementChipDescriptor[] => {
    const uniqueReactions: {
        emojiCode: string;
        userIds: string[];
    }[] = Object.values(getUniqueReactions(reactions));

    return uniqueReactions.reduce<ElementChipDescriptor[]>((descriptors, reaction) => {
        if (!reaction.emojiCode) return descriptors;

        descriptors.push({
            type: 'reaction',
            id: `reaction-${reaction.emojiCode}`,
            activationMode: 'immediate',
            emojiCode: reaction.emojiCode,
            userIds: reaction.userIds,
        });

        return descriptors;
    }, []);
};

/**
 * Gets the flat list of visible chip descriptors for a task item node's attrs.
 *
 * Parallels `getElementChipDescriptors` (`elementChipUtils.ts`) and produces the
 * same `ElementChipDescriptor` shapes from node attrs, so the task list editor
 * reuses the existing descriptor types and chip views unchanged.
 */
export const getTaskItemChipDescriptors = (attrs: TaskItemChipSource): ElementChipDescriptor[] => [
    ...getTaskDueDateChipDescriptor(attrs),
    ...getTaskAssignmentChipDescriptors(attrs),
    ...getTaskLabelChipDescriptors(attrs),
    ...getTaskReactionChipDescriptors(attrs),
];
